import * as React from 'react';

import { appBaseUrl } from 'common/utils/urls';

import { WrappedFC } from '../shared/types';

interface Props {
  /** 表示するコンポーネントの種類 */
  type: 'keyVisual' | 'icon';
  /** 画像のソース */
  imageSrc: string;
  /** 画像が表示できない場合の代替メッセージ */
  altMessage?: string;
}

/** public配下の画像のパス */
const IMAGE_BASE_PATH = '/st/images';
const DEFAULT_COMPONENT: keyof JSX.IntrinsicElements = 'div';

/**
 * 画像用コンポーネント
 */
export const Image: WrappedFC<typeof DEFAULT_COMPONENT, Props> = ({
  type,
  imageSrc,
  altMessage,
  className,
  'data-test': dataTest,
  element: Element = DEFAULT_COMPONENT,
  ...props
}) => {
  const _clsName = className || type;
  const _src = createSourcePath(imageSrc);
  return (
    <Element {...props} className={_clsName} data-test={dataTest}>
      <img src={_src} alt={altMessage} data-test={`${dataTest}-img`} />
      {props.children}
    </Element>
  );
};
Image.displayName = 'Image';

/**
 * 画像のパス作成。
 * https等のschemaを含むフルパスであれば、引数のパスをそのまま返す。
 * そうでない場合は、環境変数のベースパスがあれば付与して返す。
 *
 * ex. https://dev.sas.ibiztb.comの場合
 * https://dev.sas.ibiztb.com/sample/foo.png → https://dev.sas.ibiztb.com/sample/foo.png
 * sample/foo.png → https://dev.sas.ibiztb.com/statics/{DEPOLY_VERSION}/st/images/sample/foo.png
 */
const createSourcePath = (imageSrc: string) => {
  const isFullUri = /^\w+:\/\/.+/.test(imageSrc);
  const _path = `${IMAGE_BASE_PATH}/${imageSrc}`;
  return isFullUri ? imageSrc : appBaseUrl ? `${appBaseUrl}${_path}` : _path;
};
